import React, { useMemo } from "react";
import Select, { components } from "react-select";

// Configurações de tamanho para não "adivinhar" números no meio do código
const SIZES = {
  sm: { control: "35px", avatarOption: 32, avatarValue: 20, fontName: "14px", fontSub: "11px" },
  lg: { control: "45px", avatarOption: 40, avatarValue: 28, fontName: "16px", fontSub: "13px" }
};

const UserOption = (props) => {
  const { data, isFocused, selectProps } = props;
  const size = SIZES[selectProps.variant] || SIZES.sm; // Pega o tamanho com base na prop

  return (
    <components.UserOption {...props}>
      <div className="d-flex align-items-center gap-2">
        <img
          src={data.avatar}
          alt=""
          style={{
            width: size.avatarOption,
            height: size.avatarOption,
            borderRadius: "50%",
            objectFit: "cover"
          }}
        />
        <div className="d-flex flex-column lh-1 flex-grow-1">
          <div className="d-flex justify-content-between align-items-center">
            <span style={{
              fontWeight: 600,
              fontSize: size.fontName,
              color: isFocused ? "var(--brand-primary)" : "var(--text-primary)"
            }}>
              {data.name || data.label}
            </span>

            {data.sublabel && (
              <span style={{
                fontSize: "10px", padding: "2px 6px", borderRadius: "4px",
                backgroundColor: "var(--gray-100)", color: "var(--text-secondary)"
              }}>
                {data.sublabel}
              </span>
            )}
          </div>
          <span style={{ fontSize: size.fontSub, color: "var(--text-secondary)" }}>
            {data.email}
          </span>
        </div>
      </div>
    </components.UserOption>
  );
};

const UserSingleValue = (props) => {
  const { data, selectProps } = props;
  const size = SIZES[selectProps.variant] || SIZES.sm;

  return (
    <components.SingleValue {...props}>
      <div className="d-flex align-items-center gap-2">
        <img
          src={data.avatar}
          alt=""
          style={{ width: size.avatarValue, height: size.avatarValue, borderRadius: "50%", objectFit: "cover" }}
        />
        <span style={{ color: "var(--text-primary)", fontSize: size.fontName }}>
          {data.name || data.label}
        </span>
      </div>
    </components.SingleValue>
  );
};

const RoleOption = (props) => {
  const { data } = props;

  return (
    <components.RoleOption {...props}>
      <div className="d-flex align-items-center gap-2">
        <div className="d-flex flex-column lh-sm">
          <span style={{ fontWeight: 600, fontSize: 14, color: "#ffffff" }}>{data.label}</span>
          <span style={{ fontSize: 12, color: "#667085" }}>{data.description}</span>
        </div>
      </div>
    </components.RoleOption>
  );
};

const UserSearch = ({ data = [], onSelect, value = null, placeholder = "Pesquisar...", variant = "sm" }) => {

  const size = SIZES[variant] || SIZES.sm;

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "var(--bg-surface)",
      borderRadius: "var(--border-radius)",
      minWidth: "250px",
      border: state.isFocused ? "1px solid var(--brand-primary)" : "var(--border-thin)",
      boxShadow: "none",
      minHeight: size.control,
      height: size.control,
      "&:hover": { border: "1px solid var(--brand-primary)" },
    }),
    valueContainer: (base) => ({
      ...base,
      height: size.control,
      padding: "0 10px",
      display: "flex",
      alignItems: "center",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: size.control,
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--bg-surface)",
      color: "var(--text-primary)",
    }),
    option: (base) => ({
      ...base,
      backgroundColor: "var(--bg-surface)",
      color: "var(--text-primary)",
    }),
  };

  const options = useMemo(() => data.map((u) => {
    const fullName = u.name || u.label || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || u.user_name || "Sem nome";

    return {
      value: u.id || u.value,
      label: fullName,
      name: fullName,
      sublabel: u.sublabel || u.sector || u.department || null,
      email: u.email || "",
      avatar: u.avatar || u.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=random&color=fff`,
    };
  }), [data]);

  const selectedValue = useMemo(() => {
    if (!value) return null;
    const val = value?.value ?? value;
    return options.find((o) => String(o.value) === String(val)) || null;
  }, [value, options]);

  return (
    <Select
      variant={variant} // Passamos a prop para os sub-componentes
      classNamePrefix="user-select"
      options={options}
      value={selectedValue}
      components={{ UserOption: UserOption, SingleValue: UserSingleValue, }}
      onChange={onSelect}
      placeholder={placeholder}
      isClearable
      styles={selectStyles}
    />
  );
};

const RoleSearch = ({ data = [], onSelect, value = null, placeholder = "Search for a role..." }) => {
  const options = useMemo(
    () =>
      data.map((role) => ({
        value: role.id,
        label: role.name,
        description: role.description,
      })),
    [data]
  );

  const selectedValue = useMemo(() => {
    if (!value) {
      return null;
    }

    const selectedId = value?.value ?? value;
    return options.find((option) => String(option.value) === String(selectedId)) || null;
  }, [value, options]);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "#1d113f",
      borderRadius: "12px",
      width: "100%",
      minWidth: "200px",
      maxWidth: "500px",
      border: state.isFocused ? "1px solid #59a5ff" : "1px solid rgba(255, 255, 255, 0.1)",
      boxShadow: "none",
      minHeight: "42px",
      "&:hover": { border: "1px solid #59a5ff" },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 12px",
      display: "grid",
      overflow: "visible",
    }),

    input: (base) => ({
      ...base,
      color: "white !important",
      margin: 0,
      padding: 0,
      gridArea: "1 / 1",
    }),

    singleValue: (base) => ({
      ...base,
      color: "white",
      margin: 0,
      gridArea: "1 / 1",
    }),
    placeholder: (base, state) => ({
      ...base,
      display: state.hasValue || state.selectProps.inputValue ? "none" : "block",
      gridArea: "1 / 1",
      color: "rgba(255, 255, 255, 0.4)"
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#1d113f",
      borderRadius: "12px",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      zIndex: 9999
    })
  };

  return (
    <Select
      options={options}
      value={selectedValue}
      components={{
        UserOption: UserOption,
        SingleValue: UserSingleValue,
        RoleOption: RoleOption
      }}
      onChange={onSelect}
      placeholder={placeholder}
      isClearable
      styles={selectStyles}
    />
  );
};


const typeComponentMap = {
  users: UserSearch,
  roles: RoleSearch,
};

const AutocompleteSelect = ({ type, data, onSelect, value, placeholder, variant = "sm" }) => {

  const SelectComponent = typeComponentMap[type];
  if (!SelectComponent) {
    return null;
  }

  return (
    <SelectComponent
      data={data}
      onSelect={onSelect}
      value={value}
      placeholder={placeholder}
      variant={variant}
    />
  );
};

export default AutocompleteSelect;